import { consola } from 'consola'
import type { User, UserProfile, UpdateUserProfilePayload, UserProfileResponse, UserAddress ,
  ConsentType,
  ConsentState,
  ConsentsResponse,
  CreateOrderInput,
  CreateOrderResponse,
  GetRetryPaymentOrdersResponse,
  GetUserOrdersParams,
  GetUserOrdersResponse,
  Order,
  OrderHistoryResponse,
} from '../model/types'
import { useApi } from '@shared/api'
import { apiOrder } from '~/entities/order/@x/user'

import { formatPhoneNumber, unformatPhoneNumber } from '~/shared/lib'

const logger = consola.withTag('api-profile')

/**
 * API-клиент профиля пользователя.
 * Базовый URL: /api/v2/users
 *
 * SRP: только операции с профилем и адресами.
 * Заказы → entities/order/api/apiOrder.ts
 * Платежи → features/payment/api/
 */
class APIProfile {
  /**
   * Получить текущего пользователя.
   * GET /api/v2/users/profile
   */
  async getUser(): Promise<User> {
    const response = await useApi(true).get('/users/profile')
    const p = response.data

    return {
      id: p.id,
      first_name: p.firstName,
      last_name: p.lastName,
      default_phone_number: formatPhoneNumber(p.phoneNumber),
      default_email: p.email,
      birthday: new Date(p.birthday),
      sex: p.sex,
      delivery_address: [],
      is_email_verified: p.isEmailVerified,
      is_phone_verified: p.isPhoneVerified,
      has_password: p.hasPassword,
      points: 0,
      cashback_level: '3%',
      total_spent_current_period: 0,
      points_expiry_date: null,
    }
  }

  /**
   * Получить профиль пользователя (расширенный формат).
   * POST /api/v2/users/profile
   */
  async getUserProfile(): Promise<UserProfile> {
    const response = await useApi(true).post('/users/profile')
    return response.data
  }

  /**
   * Обновить профиль пользователя.
   * PUT /api/v2/users/profile
   */
  async updateUserProfile(payload: UpdateUserProfilePayload): Promise<UserProfileResponse> {
    const data = { ...payload }
    if (data.phoneNumber) {
      data.phoneNumber = unformatPhoneNumber(data.phoneNumber)
    }
    const response = await useApi(true).put('/users/profile', { body: data })
    return response.data
  }

  /**
   * Получить адреса доставки пользователя.
   * GET /api/v2/users/addresses
   */
  async getUserAddresses(): Promise<UserAddress[]> {
    logger.debug('Fetching user addresses')
    const response = await useApi(true).get('/users/addresses')

    if (Array.isArray(response.data)) return response.data
    if (Array.isArray(response.data?.addresses)) return response.data.addresses

    logger.error('Unexpected address format:', response.data)
    throw new Error('Invalid addresses format received from API')
  }

  /**
   * Удалить профиль пользователя.
   * DELETE /api/v2/users/profile
   */
  async deleteUserProfile(): Promise<UserProfileResponse> {
    const response = await useApi(true).delete('/users/profile')
    return response.data
  }

  /**
   * Текущие согласия пользователя.
   * GET /api/v2/users/consents
   */
  async getConsents(): Promise<ConsentsResponse> {
    const response = await useApi(true).get<ConsentsResponse>('/users/consents')
    return response.data
  }

  /**
   * Дать или отозвать согласие.
   * POST /api/v2/users/consents
   *
   * Согласие на рекламу — отдельное от согласия на обработку данных: первое
   * разрешает слать акции, второе — вести аккаунт. Отзыв рекламного согласия
   * не трогает сервисные уведомления о заказе.
   */
  async updateConsent(type: ConsentType, granted: boolean): Promise<ConsentState> {
    const response = await useApi(true).post<ConsentState>('/users/consents', {
      body: { type, granted },
    })
    return response.data
  }

  // --- Legacy-совместимость со старым APIProfile ---

  async createOrder(orderData: CreateOrderInput): Promise<CreateOrderResponse> {
    const response = await useApi(true).post('/orders', { body: orderData })
    return response.data
  }

  async getOrderDetails(orderId: string): Promise<Order> {
    const response = await useApi(true).get(`/orders/${orderId}`)
    if (response.data?.success && response.data?.order) {
      return response.data.order
    }
    return response.data
  }

  async retryPayment(userId: string): Promise<GetRetryPaymentOrdersResponse> {
    const response = await useApi(true).get(`/orders/user/${userId}/retry-payment`)
    return response.data
  }

  async getOrder(orderId: string | number): Promise<{ status: string; paymentStatus: string; payment_status: string }> {
    const order = await this.getOrderDetails(String(orderId))
    const paymentStatus = String((order as any).paymentStatus ?? (order as any).payment_status ?? '')
    const status = String((order as any).status ?? '')
    return { status, paymentStatus, payment_status: paymentStatus }
  }

  /**
   * История заказов / пополнений.
   * - Объект GetUserOrdersParams → GET /orders/user (v2)
   * - userId + { page, limit, details } → тот же список, формат OrderHistoryResponse (экран activation-history)
   * - только userId → legacy v1, ожидается { data: { transactions } } (replenishment-stories)
   */
  async getOrderHistory(
    userIdOrParams: GetUserOrdersParams | string | number,
    opts?: { details?: string; limit?: number; page?: number },
  ): Promise<GetUserOrdersResponse | OrderHistoryResponse | { data: { transactions: any[] } }> {
    if (userIdOrParams !== null && typeof userIdOrParams === 'object' && 'userId' in userIdOrParams) {
      return apiOrder.getOrderHistory(userIdOrParams as GetUserOrdersParams)
    }

    const userId = String(userIdOrParams)

    if (opts !== undefined && (opts.page !== undefined || opts.limit !== undefined || opts.details !== undefined)) {
      const limit = opts.limit ?? 10
      const page = opts.page ?? 1
      const offset = (page - 1) * limit
      const res = await apiOrder.getOrderHistory({ userId, limit, offset })
      return {
        success: res.success,
        orders: res.orders.map(o => ({
          id: Number(o.id) || (o.id as unknown as number),
          orderType: o.orderType,
          status: o.status,
          paymentStatus: String(o.paymentStatus),
          totalAmount: o.totalAmount,
          createdAt: o.createdAt,
          itemsCount: o.items?.length ?? 0,
          items: o.items,
          branch: o.branch as OrderHistoryResponse['orders'][0]['branch'],
          delivery_address: o.deliveryAddress,
          payment_method: String(o.paymentMethod ?? ''),
        })),
      } as OrderHistoryResponse
    }

    const response = await useApi(false).get<any>(`/user/${userId}/payment-history`)
    const body = response.data
    if (body?.data?.transactions) return body
    if (Array.isArray(body?.transactions)) return { data: { transactions: body.transactions } }
    return { data: { transactions: [] } }
  }

  addPushToken(data: { token: string; platform?: string; deviceId?: string; deviceName?: string }) {
    return useApi(true).post('/users/push-tokens', { body: data })
  }

  removePushToken(data: { token: string }) {
    return useApi(true).delete('/users/push-tokens', { body: data })
  }

  /** @deprecated */
  updateEmail(email: string) {
    return useApi().put('/user/update-email', { body: { email } })
  }

  /** @deprecated */
  getSmsAttempts(): Promise<{ attemptsRemaining: number }> {
    return useApi().get('/auth/sms-attempts').then(r => r.data)
  }

  /** @deprecated */
  sendPhoneConfirmation(phone: string) {
    return useApi().post('/user/send-phone-confirmation', { body: { phone } })
  }

  /** @deprecated */
  confirmPhone(phone: string, code: string) {
    return useApi().put('/user/confirm-phone', { body: { phone, code } })
  }
}

export const apiProfile = new APIProfile()
export default apiProfile
